import type { AppData, ConnectorData } from "~/slices/appDesignSlice";

export type DiagramVariant = "portal" | "compact";

type LayoutConfig = {
  cardWidth: number;
  cardHeight: number;
  gapX: number;
  gapY: number;
};

const LAYOUT_CONFIGS: Record<DiagramVariant, LayoutConfig> = {
  portal: { cardWidth: 110, cardHeight: 100, gapX: 180, gapY: 80 },
  compact: { cardWidth: 160, cardHeight: 52, gapX: 120, gapY: 60 },
};

export const getLayoutConfig = (variant: DiagramVariant): LayoutConfig => LAYOUT_CONFIGS[variant];

export const CARD_WIDTH = 110;
export const CARD_HEIGHT = 100;
const GAP_X = 50;
const GAP_Y = 60;
const PADDING = 24;

/** Filter to top N apps by connection count (in-degree + out-degree) */
export const filterTopApps = (
  apps: AppData[],
  connectors: ConnectorData[],
  limit: number,
): { apps: AppData[]; connectors: ConnectorData[]; overflowCount: number } => {
  if (apps.length <= limit) {
    const appIds = new Set(apps.map((a) => a.appid));
    const validConnectors = connectors.filter((c) => appIds.has(c.sid) && appIds.has(c.tid));
    return { apps, connectors: validConnectors, overflowCount: 0 };
  }

  const connectionCount = new Map<number, number>();
  for (const app of apps) connectionCount.set(app.appid, 0);
  for (const c of connectors) {
    connectionCount.set(c.sid, (connectionCount.get(c.sid) ?? 0) + 1);
    connectionCount.set(c.tid, (connectionCount.get(c.tid) ?? 0) + 1);
  }

  const sorted = [...apps].sort((a, b) => {
    const diff = (connectionCount.get(b.appid) ?? 0) - (connectionCount.get(a.appid) ?? 0);
    return diff !== 0 ? diff : a.appid - b.appid;
  });

  const topApps = sorted.slice(0, limit);
  const topIds = new Set(topApps.map((a) => a.appid));
  const filteredConnectors = connectors.filter((c) => topIds.has(c.sid) && topIds.has(c.tid));

  return {
    apps: topApps,
    connectors: filteredConnectors,
    overflowCount: apps.length - limit,
  };
};

/** Assign each app to a layer (depth) via BFS from root nodes */
export const assignLayers = (apps: AppData[], connectors: ConnectorData[]): Map<number, number> => {
  const layers = new Map<number, number>();

  if (connectors.length === 0) {
    for (const app of apps) layers.set(app.appid, 0);
    return layers;
  }

  const connectedIds = new Set<number>();
  const outEdges = new Map<number, number[]>();
  const inDegree = new Map<number, number>();

  for (const c of connectors) {
    connectedIds.add(c.sid);
    connectedIds.add(c.tid);
    if (!outEdges.has(c.sid)) outEdges.set(c.sid, []);
    outEdges.get(c.sid)!.push(c.tid);
    inDegree.set(c.tid, (inDegree.get(c.tid) ?? 0) + 1);
  }

  const roots = Array.from(connectedIds).filter(
    (id) => !inDegree.has(id) || inDegree.get(id) === 0,
  );
  if (roots.length === 0 && connectedIds.size > 0) {
    roots.push(Array.from(connectedIds)[0]!);
  }

  const queue: { id: number; depth: number }[] = roots.map((id) => ({ id, depth: 0 }));
  const visited = new Set<number>();

  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    layers.set(id, depth);
    for (const tid of outEdges.get(id) ?? []) {
      if (!visited.has(tid)) queue.push({ id: tid, depth: depth + 1 });
    }
  }

  const maxLayer = layers.size > 0 ? Math.max(...Array.from(layers.values())) : 0;
  const isolatedLayer = connectors.length > 0 ? maxLayer + 1 : 0;
  for (const app of apps) {
    if (!layers.has(app.appid)) layers.set(app.appid, isolatedLayer);
  }

  return layers;
};

/** Count edge crossings between two adjacent layers given the current node orderings. */
const countCrossings = (
  upperLayer: number[],
  lowerLayer: number[],
  connectors: ConnectorData[],
): number => {
  const upperIdx = new Map<number, number>();
  for (let i = 0; i < upperLayer.length; i++) upperIdx.set(upperLayer[i]!, i);
  const lowerIdx = new Map<number, number>();
  for (let i = 0; i < lowerLayer.length; i++) lowerIdx.set(lowerLayer[i]!, i);

  // Collect edges that span these two layers
  const edges: { u: number; l: number }[] = [];
  for (const c of connectors) {
    if (upperIdx.has(c.sid) && lowerIdx.has(c.tid)) {
      edges.push({ u: upperIdx.get(c.sid)!, l: lowerIdx.get(c.tid)! });
    } else if (upperIdx.has(c.tid) && lowerIdx.has(c.sid)) {
      edges.push({ u: upperIdx.get(c.tid)!, l: lowerIdx.get(c.sid)! });
    }
  }

  let crossings = 0;
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const ei = edges[i]!;
      const ej = edges[j]!;
      if ((ei.u - ej.u) * (ei.l - ej.l) < 0) crossings++;
    }
  }
  return crossings;
};

/** Sum of index distances for same-layer edges (lower is better). */
const sameLayerCost = (layer: number[], connectors: ConnectorData[]): number => {
  const idx = new Map<number, number>();
  for (let i = 0; i < layer.length; i++) idx.set(layer[i]!, i);
  let cost = 0;
  for (const c of connectors) {
    if (idx.has(c.sid) && idx.has(c.tid)) {
      cost += Math.abs(idx.get(c.sid)! - idx.get(c.tid)!);
    }
  }
  return cost;
};

/** Reorder nodes within each layer to minimize edge crossings and same-layer edge distances.
 * Uses barycenter heuristic + adjacent-swap local optimization. */
export const reorderByBarycenter = (
  layerGroups: number[][],
  connectors: ConnectorData[],
  iterations = 4,
): void => {
  // Build adjacency: for each node, collect all connected node ids
  const neighbors = new Map<number, number[]>();
  for (const c of connectors) {
    if (!neighbors.has(c.sid)) neighbors.set(c.sid, []);
    if (!neighbors.has(c.tid)) neighbors.set(c.tid, []);
    neighbors.get(c.sid)!.push(c.tid);
    neighbors.get(c.tid)!.push(c.sid);
  }

  const indexOf = (layer: number[], id: number): number => layer.indexOf(id);

  const sortByBarycenter = (layer: number[], refLayer: number[]): void => {
    const barycenters = new Map<number, number>();
    for (const nodeId of layer) {
      const nbrs = (neighbors.get(nodeId) ?? []).filter((n) => refLayer.includes(n));
      if (nbrs.length === 0) {
        barycenters.set(nodeId, indexOf(layer, nodeId));
      } else {
        const avg = nbrs.reduce((sum, n) => sum + indexOf(refLayer, n), 0) / nbrs.length;
        barycenters.set(nodeId, avg);
      }
    }
    layer.sort((a, b) => (barycenters.get(a) ?? 0) - (barycenters.get(b) ?? 0));
  };

  // Phase 1: Barycenter sweeps for cross-layer crossing minimization
  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 1; i < layerGroups.length; i++) {
      sortByBarycenter(layerGroups[i]!, layerGroups[i - 1]!);
    }
    for (let i = layerGroups.length - 2; i >= 0; i--) {
      sortByBarycenter(layerGroups[i]!, layerGroups[i + 1]!);
    }
  }

  // Phase 2: Adjacent-swap to reduce crossings and same-layer edge distances
  const totalCost = (): number => {
    let cost = 0;
    for (let i = 0; i < layerGroups.length - 1; i++) {
      cost += countCrossings(layerGroups[i]!, layerGroups[i + 1]!, connectors);
    }
    for (const layer of layerGroups) {
      cost += sameLayerCost(layer, connectors);
    }
    return cost;
  };

  for (let pass = 0; pass < iterations; pass++) {
    let improved = false;
    for (const layer of layerGroups) {
      for (let i = 0; i < layer.length - 1; i++) {
        const before = totalCost();
        // Try swap
        [layer[i], layer[i + 1]] = [layer[i + 1]!, layer[i]!];
        const after = totalCost();
        if (after < before) {
          improved = true;
        } else {
          // Revert
          [layer[i], layer[i + 1]] = [layer[i + 1]!, layer[i]!];
        }
      }
    }
    if (!improved) break;
  }
};

/** Compute x, y position for each node, with barycenter-optimized ordering */
export const computeNodePositions = (
  layers: Map<number, number>,
  config?: LayoutConfig,
  connectors?: ConnectorData[],
): Map<number, { x: number; y: number }> => {
  const cw = config?.cardWidth ?? CARD_WIDTH;
  const ch = config?.cardHeight ?? CARD_HEIGHT;
  const gx = config?.gapX ?? GAP_X;
  const gy = config?.gapY ?? GAP_Y;

  const positions = new Map<number, { x: number; y: number }>();
  const maxLayer = Math.max(...Array.from(layers.values()), 0);

  const layerGroups: number[][] = [];
  for (let i = 0; i <= maxLayer; i++) layerGroups.push([]);
  for (const [id, layer] of Array.from(layers.entries())) layerGroups[layer]?.push(id);
  // Initial order by appid
  for (const group of layerGroups) group.sort((a, b) => a - b);

  // Apply barycenter crossing minimization if connectors are provided
  if (connectors && connectors.length > 0) {
    reorderByBarycenter(layerGroups, connectors);
  }

  const maxNodesInLayer = Math.max(...layerGroups.map((l) => l.length), 1);
  const maxLayerWidth = maxNodesInLayer * cw + (maxNodesInLayer - 1) * gx;

  for (let li = 0; li < layerGroups.length; li++) {
    const group = layerGroups[li]!;
    if (group.length === 0) continue;
    const groupWidth = group.length * cw + (group.length - 1) * gx;
    const startX = PADDING + (maxLayerWidth - groupWidth) / 2;

    for (let ni = 0; ni < group.length; ni++) {
      positions.set(group[ni]!, {
        x: startX + ni * (cw + gx),
        y: PADDING + li * (ch + gy),
      });
    }
  }

  return positions;
};

/** Compute SVG line endpoints between two positioned nodes.
 * Uses vertical (bottom→top) connections for hierarchical layout.
 * Falls back to horizontal (left→right) when nodes are on the same layer (same y).
 * @param sourceOffset - perpendicular offset at the source node
 * @param targetOffset - perpendicular offset at the target node */
export const computeEdge = (
  source: { x: number; y: number },
  target: { x: number; y: number },
  cardWidth = CARD_WIDTH,
  cardHeight = CARD_HEIGHT,
  sourceOffset = 0,
  targetOffset = 0,
): { x1: number; y1: number; x2: number; y2: number } => {
  const sCx = source.x + cardWidth / 2;
  const tCx = target.x + cardWidth / 2;
  const dy = target.y - source.y;

  // Same layer (horizontal): use left/right edges, offset on Y axis
  if (dy === 0) {
    const dx = target.x - source.x;
    if (dx > 0) {
      return {
        x1: source.x + cardWidth,
        y1: source.y + cardHeight / 2 + sourceOffset,
        x2: target.x,
        y2: target.y + cardHeight / 2 + targetOffset,
      };
    }
    return {
      x1: source.x,
      y1: source.y + cardHeight / 2 + sourceOffset,
      x2: target.x + cardWidth,
      y2: target.y + cardHeight / 2 + targetOffset,
    };
  }

  // Different layers: use bottom/top edges, offset on X axis
  if (dy > 0) {
    return {
      x1: sCx + sourceOffset,
      y1: source.y + cardHeight,
      x2: tCx + targetOffset,
      y2: target.y,
    };
  }
  return {
    x1: sCx + sourceOffset,
    y1: source.y,
    x2: tCx + targetOffset,
    y2: target.y + cardHeight,
  };
};

/** Compute label position at edge midpoint */
export const computeEdgeLabelPosition = (edge: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): { x: number; y: number } => ({
  x: (edge.x1 + edge.x2) / 2,
  y: (edge.y1 + edge.y2) / 2,
});

export type LabelEntry = { relationship: string; color: string };
export type LabelGroup = {
  key: string;
  pos: { x: number; y: number };
  labels: LabelEntry[];
};

/** Merge label groups whose positions are close enough to visually overlap.
 *  Uses proximity thresholds based on typical label dimensions. */
const LABEL_MERGE_THRESHOLD_X = 40;
const LABEL_MERGE_THRESHOLD_Y = 18;

export const mergeLabelGroups = (groups: LabelGroup[]): LabelGroup[] => {
  const merged: LabelGroup[] = [];
  for (const group of groups) {
    const existing = merged.find(
      (m) =>
        Math.abs(m.pos.x - group.pos.x) < LABEL_MERGE_THRESHOLD_X &&
        Math.abs(m.pos.y - group.pos.y) < LABEL_MERGE_THRESHOLD_Y,
    );
    if (existing) {
      existing.labels.push(...group.labels);
    } else {
      merged.push({ key: group.key, pos: { ...group.pos }, labels: [...group.labels] });
    }
  }
  return merged;
};

export type GroupedEdge = {
  connector: ConnectorData;
  groupIndex: number;
  groupCount: number;
};

/** Group connectors by normalized pair key (min(sid,tid)-max(sid,tid)) */
export const groupEdgesByPair = (connectors: ConnectorData[]): GroupedEdge[] => {
  const groups = new Map<string, ConnectorData[]>();
  for (const c of connectors) {
    const key = `${Math.min(c.sid, c.tid)}-${Math.max(c.sid, c.tid)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(c);
  }

  const result: GroupedEdge[] = [];
  for (const c of connectors) {
    const key = `${Math.min(c.sid, c.tid)}-${Math.max(c.sid, c.tid)}`;
    const group = groups.get(key)!;
    result.push({
      connector: c,
      groupIndex: group.indexOf(c),
      groupCount: group.length,
    });
  }
  return result;
};
